import React, { useContext, useEffect, useState } from 'react';
import classNames from 'classnames';
import { ArrowLeft } from '@styled-icons/feather';
import { useTheme } from 'next-themes';
import mixpanel from 'mixpanel-browser';
import ClientOnlyPortal from '@/components/ClientOnlyPortal';
import styles from './CollectionTemplateSidebar.module.css';
import { DEFAULT_BIG_SIDEBAR_WIDTH, SIDEBAR_ZINDEX_OPEN, DARK_THEME } from '../../constants';

import { CollectionTemplateSidebarContext, CollectionTemplateSidebarDispatchContext, CollectionTemplateSidebarEnabledContext } from '../../context/CollectionTemplateContextProvider';
import SidebarItem from '../SidebarItem';
import COLLECTION_TEMPLATES from '../../../CollectionSettingsComponents/collectionViewTemplates';
import TemplateItem from './TemplateItem';
import Overlay from '../Overlay';
import { CollectionTemplatePathContext, CollectionTemplatePathDispatchContext } from '../../context/CollectionTemplateContextProvider/pathContext';
import { CollectionTemplateHoveredDispatchContext } from '../../context/CollectionTemplateContextProvider/hoveredContext';
import { ICollectionTemplateList, TEMPLATE_CATEGORY } from '../../../CollectionSettingsComponents/collectionViewTemplates/types';
import EditorTool from '../../EditorTool';
import { EVENT } from '../../../../analytics';
import { BLOCK_ELEMENT_KEY } from '../../types';
import ButtonBoxBorder from '../../../Button/ButtonBoxBorder';
import { CollectionSettingsPathDispatchContext } from '../../context/CollectionSettingsContextProvider/pathContext';
import { CollectionSetupFlowOpenDispatchContext } from '../../../CollectionSetupFlow/context/openContext';
import { COLLECTION_START_FROM_SCRATCH } from '../../../../constants';
import { useTEditorRef } from '../../plate';

/**
 *
 * CollectionTemplateSidebar requires
 * CollectionTemplateContext and CollectionTemplateDispatchContext to be provided
 * You can use CollectionTemplateContextProvider to provide both context.
 */
function CollectionTemplateSidebar() {
  const editor = useTEditorRef();
  const enabled = useContext(CollectionTemplateSidebarEnabledContext);
  const sidebarOpen = useContext(CollectionTemplateSidebarContext);
  const setsidebarOpen = useContext(CollectionTemplateSidebarDispatchContext);
  const templatePath = useContext(CollectionTemplatePathContext);
  const settemplatePath = useContext(CollectionTemplatePathDispatchContext);
  const sethovered = useContext(CollectionTemplateHoveredDispatchContext);
  const setcollectionSettingsPath = useContext(CollectionSettingsPathDispatchContext);
  const setcollectionSetupFlowOpen = useContext(CollectionSetupFlowOpenDispatchContext);

  const [category, setcategory] = useState(TEMPLATE_CATEGORY.BLOG);
  const { theme } = useTheme();

  const closeSidebar = () => {
    setsidebarOpen(false);
    settemplatePath(null);
  };

  useEffect(() => {
    sethovered(null);
    if (sidebarOpen) {
      mixpanel.track(EVENT.OPEN_CMS, {
        cms_entry_point: 'editor',
      });
    }
  }, [sidebarOpen]);

  const onSelectTemplate = (template: ICollectionTemplateList) => {
    EditorTool.setBlockProperty(
      editor,
      BLOCK_ELEMENT_KEY.COLLECTION_TEMPLATE,
      {
        ...template.template,
        templateKey: `${category}:${template.key}`,
      },
      { at: templatePath },
    );
    EditorTool.setBlockProperty(
      editor,
      BLOCK_ELEMENT_KEY.COLLECTION_DEFAULT_ITEM_PAGE_TEMPLATE_KEY,
      `${template.defaultItemPageTemplate?.category}:${template.defaultItemPageTemplate?.key}`,
      { at: templatePath },
    );

    setcollectionSettingsPath(templatePath);
    setcollectionSetupFlowOpen(true);
    closeSidebar();
  };

  const onSelectTemplateScratch = () => {
    EditorTool.setBlockProperty(
      editor,
      BLOCK_ELEMENT_KEY.COLLECTION_TEMPLATE,
      { templateKey: COLLECTION_START_FROM_SCRATCH },
      { at: templatePath },
    );

    setcollectionSettingsPath(templatePath);
    setcollectionSetupFlowOpen(true);
    closeSidebar();
  };
  if (!enabled) {
    // eslint-disable-next-line react/jsx-no-useless-fragment
    return <></>;
  }
  return (
    <ClientOnlyPortal>
      {sidebarOpen && <Overlay onClick={closeSidebar} zIndex={SIDEBAR_ZINDEX_OPEN - 1} />}

      <div
        className={styles.sidebar}
        style={{
          width: DEFAULT_BIG_SIDEBAR_WIDTH,
          left: sidebarOpen ? 0 : -DEFAULT_BIG_SIDEBAR_WIDTH - 10,
          zIndex: SIDEBAR_ZINDEX_OPEN,
        }}
      >
        <div className={styles.block}>
          <div
            data-testid="CollectionTemplateSidebar_title"
            role="none"
            className={classNames(
              'with-cursor-pointer is-flex is-flex-direction-row is-justify-content-flex-start is-align-items-center is-size-6 with-light-text-color',
            )}
            onClick={() => setsidebarOpen(false)}
          >
            <ArrowLeft
              size={18}
              style={{ marginRight: 10, cursor: 'pointer' }}
            />
            Collection Templates
          </div>
        </div>
        <div className={classNames(styles.sidebarMiddle, 'is-flex is-flex-direction-row is-align-items-flex-start is-justify-content-flex-start')}>
          <div className={classNames(styles.block, styles.blockOptions)}>
            {
              Object.entries(COLLECTION_TEMPLATES).map(([k, v]) => (
                <SidebarItem
                  key={k}
                  selected={category === k}
                  center={v.title}
                  className="mt-1 mb-1 is-subtitle-size with-text-color"
                  onClick={() => setcategory(k as TEMPLATE_CATEGORY)}
                />
              ))
            }
          </div>
          <div className={classNames(styles.block, styles.blockTemplates)}>
            {
              Object.entries(COLLECTION_TEMPLATES[category]?.templates).map(([k, v]) => (
                <TemplateItem
                  key={k}
                  img={(theme === DARK_THEME && v.imageSidebarDark)
                    || (theme === DARK_THEME && v.imageDark)
                    || v.imageSidebar
                    || v.image}
                  onHover={() => sethovered(v)}
                  onMouseLeave={() => sethovered(null)}
                  onSelectTemplate={() => {
                    mixpanel.track(EVENT.CLICK_COLLECTION_TEMPLATE, {
                      collection_template_name: k,
                    });
                    onSelectTemplate(v);
                  }}
                  active={false}
                />
              ))
            }
          </div>
        </div>

        <div className={classNames('p-3 pt-5 is-flex is-align-items-center is-justify-content-center', styles.sidebarBottom)}>
          <ButtonBoxBorder style={{ width: '100%' }} onClick={onSelectTemplateScratch}>
            Start from scratch &rarr;
          </ButtonBoxBorder>
        </div>
      </div>
    </ClientOnlyPortal>
  );
}

export default CollectionTemplateSidebar;
